import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Input } from "antd";
import { FiDownload, FiSearch } from "react-icons/fi";
import DashboardFilters from "../dashboard/components/DashboardFilters.jsx";
import TableWithSkeleton from "../../../components/global/table/TableWithSkeleton.jsx";
import { exportAsCSV } from "../../../services/exportAsCSV";
import { getResponseData } from "../../../utils/apiHelpers.js";
import { GetFtdReportApi } from "../../../api/request/salesDashboard/reports.js";
import {
  GetAllOffices,
  GetAllTeamLeaders,
} from "../../../api/request/subadmin.js";
import {
  GetSalesAgentsApi,
  buildSalesAgentsParams,
  mapSalesAgentsToOptions,
} from "../../../api/request/salesDashboard/dashboard.js";
import { getFtdReportColumns } from "../../../columns/salesDashboard/reports/ftdReportColumns.jsx";
import { useUserStore } from "../../../store/userStore.js";

const buildQuery = (params) =>
  new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(
        ([, value]) => value !== undefined && value !== null && value !== "",
      ),
    ),
  ).toString();

const FTDReport = () => {
  const userDetails = useUserStore((state) => state.userDetails);
  const isSales = userDetails?.department === "Sales";
  const isAgent = isSales && !userDetails?.isTeamLeader;
  const isTeamLeader = isSales && userDetails?.isTeamLeader;
  const teamLeadId = isSales ? userDetails?.userId : undefined;
  const [isLoading, setIsLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [offices, setOffices] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({
    from: undefined,
    to: undefined,
    officeId: undefined,
    teamLeadId: teamLeadId,
    agentId: undefined,
    search: "",
    page: 1,
    pageSize: 10,
  });

  const fetchOffices = async () => {
    try {
      const data = getResponseData(await GetAllOffices());
      if (data?.success) {
        setOffices(
          (data?.data || []).map((item) => ({
            label: item?.officeName || item?.name,
            value: item?.id || item?.officeId,
          })),
        );
      }
    } catch (error) {
      setOffices([]);
    }
  };

  useEffect(() => {
    fetchOffices();
  }, []);

  useEffect(() => {
    const fetchTeamLeaders = async () => {
      if (!filters.officeId) {
        setTeamLeaders([]);
        return;
      }
      try {
        const data = getResponseData(await GetAllTeamLeaders(filters.officeId));
        if (data?.success) {
          setTeamLeaders(
            (data?.data || []).map((item) => ({
              label: item?.fullName || item?.teamLeadName,
              value: item?.userId || item?.teamLeadId,
            })),
          );
        }
      } catch (error) {
        setTeamLeaders([]);
      }
    };

    fetchTeamLeaders();
  }, [filters.officeId]);

  useEffect(() => {
    const fetchAgents = async () => {
      if (!filters.teamLeadId) {
        setAgents([]);
        return;
      }
      try {
        const data = getResponseData(
          await GetSalesAgentsApi(buildSalesAgentsParams(filters)),
        );
        if (data?.success) {
          setAgents(mapSalesAgentsToOptions(data?.data));
        }
      } catch (error) {
        setAgents([]);
      }
    };

    fetchAgents();
  }, [filters.teamLeadId]);

  useEffect(() => {
    let cancelled = false;

    const fetchFtdReport = async () => {
      setIsLoading(true);
      try {
        const query = buildQuery(filters);
        const data = getResponseData(await GetFtdReportApi(query));
        if (cancelled) return;

        if (data?.success) {
          setRows(data?.data || []);
          setTotalRows(Number(data?.totalRecords || 0));
        } else {
          setRows([]);
          setTotalRows(0);
        }
      } catch (error) {
        if (!cancelled) {
          setRows([]);
          setTotalRows(0);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchFtdReport();

    return () => {
      cancelled = true;
    };
  }, [
    filters.from,
    filters.to,
    filters.officeId,
    filters.teamLeadId,
    filters.agentId,
    filters.search,
    filters.page,
    filters.pageSize,
  ]);

  const handleFiltersChange = useCallback((updater) => {
    setFilters((prev) => {
      const next =
        typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      return { ...next, page: 1 };
    });
  }, []);

  const mapExportRows = (records = []) =>
    records.map((item) => ({
      client: item?.clientName || "--",
      country: item?.country || "--",
      accountManager: item?.accountManager || "--",
      team: item?.team || "--",
      office: item?.office || "--",
      amount: item?.amount || "--",
      date: item?.date ? String(item.date).slice(0, 10) : "--",
      method: item?.method || "--",
      account: item?.account || "--",
      source: item?.source || "--",
      salesOwner: item?.salesOwner || "--",
    }));

  const handleExportCsv = async () => {
    setIsExportLoading(true);
    try {
      const exportQuery = buildQuery({
        ...filters,
        page: 1,
        pageSize: totalRows || 1000,
      });
      const data = getResponseData(await GetFtdReportApi(exportQuery));
      const exportData = mapExportRows(data?.data || []);
      if (exportData.length) {
        exportAsCSV(
          exportData,
          [],
          `ftd-report-${new Date().toISOString().slice(0, 10)}.csv`,
        );
      }
    } finally {
      setIsExportLoading(false);
    }
  };

  const exportMenu = {
    items: [{ key: "csv", label: "Export CSV" }],
    onClick: ({ key }) => {
      if (key === "csv") {
        handleExportCsv();
      }
    },
  };

  const columns = useMemo(() => getFtdReportColumns(), []);

  return (
    <div className="space-y-4">
      <DashboardFilters
        isTeamLeader={isTeamLeader}
        isAgent={isAgent}
        isSales={isSales}
        filters={filters}
        setFilters={handleFiltersChange}
        offices={offices}
        teamLeaders={teamLeaders}
        agents={agents}
        rightActions={
          <div className="flex items-center gap-2">
            <Input
              allowClear
              value={filters.search}
              onChange={(event) =>
                setFilters((prev) => ({
                  ...prev,
                  search: event.target.value,
                  page: 1,
                }))
              }
              placeholder="Search client or account"
              prefix={<FiSearch size={14} />}
              className="w-56"
            />
            <Dropdown
              menu={exportMenu}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button
                icon={<FiDownload />}
                type="primary"
                loading={isExportLoading}
              >
                Export
              </Button>
            </Dropdown>
          </div>
        }
      />

      <div className="border-2 border-light rounded-xl p-4 overflow-hidden">
        <div className="overflow-hidden rounded-xl">
          <TableWithSkeleton
            showIndexColumn={false}
            rowKey="userId"
            columns={columns}
            data={rows}
            loading={isLoading}
            page={filters.page}
            pageSize={filters.pageSize}
            pagination={{
              current: filters.page,
              pageSize: filters.pageSize,
              total: totalRows,
              showSizeChanger: true,
            }}
            onChange={(pagination) => {
              setFilters((prev) => ({
                ...prev,
                page: pagination?.current || 1,
                pageSize: pagination?.pageSize || 10,
              }));
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default FTDReport;
